import type { DocNode } from '@atlaskit/adf-schema';
import type { JSONDocNode } from '@atlaskit/editor-json-transformer';
import { fg } from '@atlaskit/platform-feature-flags';
import type { CommandProperties } from '@atlassian/rovo-content-bridge-api';
import type { PageContentCommandResult } from '@atlassian/rovo-content-bridge-api-commands';

import { log } from '../utils/aifc-logging';
import { getDocument } from '../utils/bridge-document-utils';

import { CommandHandler, type PassThroughCommandProps } from './command-handler';

export const buildContentContext = async ({
	props,
	doc,
	selectionFragment,
	selectionLocalIds,
	selectionMarkdown,
}: {
	doc: DocNode | JSONDocNode | undefined;
	props: PassThroughCommandProps;
	selectionFragment: string;
	selectionLocalIds: string;
	selectionMarkdown: string;
}): Promise<PageContentCommandResult> => {
	let additionalContextFromProps = props.additionalContext || { isViewMode: false };
	if (typeof additionalContextFromProps === 'function') {
		if (fg('platform_editor_ai_aifc_patch_ga_blockers')) {
			try {
				additionalContextFromProps = await additionalContextFromProps();
				// eslint-disable-next-line no-unused-vars
			} catch (err) {
				additionalContextFromProps = {};
			}
		} else {
			additionalContextFromProps = await additionalContextFromProps();
		}
	}
	const { isViewMode = false, ...additionalContext } = additionalContextFromProps || {};

	return {
		contentId: props.contentId,
		contentType: props.contentType,
		pageBody: doc,
		selectionFragment,
		selectionLocalIds,
		selectionMarkdown,
		isViewMode,
		useGenericEditorSkill: props.useGenericEditorSkill,
		additionalContext: Object.keys(additionalContext).length > 0 ? additionalContext : undefined,
	};
};

export class ReadContentCommandHandler extends CommandHandler<
	CommandProperties,
	PageContentCommandResult
> {
	async execute(): Promise<PageContentCommandResult> {
		const defaultResult: PageContentCommandResult = await buildContentContext({
			props: this.props,
			doc: undefined,
			selectionFragment: '',
			selectionLocalIds: '',
			selectionMarkdown: '',
		});

		try {
			const doc = fg('platform_editor_aifc_rovo_bridge_partial_doc')
				? this.props.editorAPI?.aiExperience?.actions.getPartialDocumentFromSelection()
				: fg('platform_editor_ai_aifc_patch_ga_blockers')
					? await this.props.editorAPI.core.actions.requestDocumentAsync()
					: await getDocument(this.props.editorAPI);
			if (!doc) {
				// TODO: EDITOR-123 - report this error
				return defaultResult;
			}

			const selection = this.props.editorAPI.aiExperience?.actions.getAISelectionContext();
			const content: PageContentCommandResult = await buildContentContext({
				props: this.props,
				doc,
				selectionFragment: selection?.selectionFragment
					? JSON.stringify(selection.selectionFragment)
					: '',
				selectionLocalIds: selection?.localIds ? JSON.stringify(selection.localIds) : '',
				selectionMarkdown: selection?.selectionMarkdown ?? '',
			});
			log('handle read-content-command result', {
				additionalContext: this.props.additionalContext,
				doc,
				selection,
				content,
			});

			return content ?? defaultResult;
		} catch {
			// TODO: EDITOR-123 - report this error
			return defaultResult;
		}
	}
}
